import React, { useContext, useEffect, useRef, useState } from "react";
import { useGetLink } from "../../utils/useOpenLink";
import { Link } from "react-router-dom";
import clsx from "clsx";
import { WindowContext } from "../WindowResize/resize";

function SubMenuStyle4({ data }) {
    const { getLink } = useGetLink();
    const containerRef = useRef(null);
    const [selectedContent, setSelectedContent] = useState(null);
    const [availableHeight, setAvailableHeight] = useState("100px");
    const { isMobile } = useContext(WindowContext);
    const [showData, setShowData] = useState(!isMobile);
    const [featureItem, setFeatureItem] = useState(null);

    useEffect(() => {
        if (isMobile) {
            const timer = setTimeout(() => setShowData(true), 500);
            return () => clearTimeout(timer);
        }
    }, [isMobile]);

    // Set default selected content for desktop
    useEffect(() => {
        if (!isMobile && data.contents && data.contents.length > 0) {
            setSelectedContent(data.contents[0]);
        }
    }, [data.contents, isMobile]);

    useEffect(() => {
        const filterFeatureItem = data.contents.filter((e) => e.feature === 1);
        if (filterFeatureItem.length > 0) {
            setFeatureItem(filterFeatureItem[0]);
        } else {
            setFeatureItem(null);
        }
    }, [data.contents]);

    useEffect(() => {
        const calculateHeight = () => {
            const containerTop = containerRef.current?.getBoundingClientRect().top || 0;
            const footerHeight = document.getElementById("footer")?.offsetHeight || 0;
            const newHeight = window.innerHeight - containerTop - footerHeight;

            setAvailableHeight(`${newHeight}px`);
        };

        // Trigger height calculation only after the data has been shown
        if (showData) {
            calculateHeight();
        }

        window.addEventListener("resize", calculateHeight);

        return () => window.removeEventListener("resize", calculateHeight);
    }, [showData]); // Recalculate height whenever showData changes

    const handleMainHeadingClick = (content) => {
        setSelectedContent((prev) => (prev?.id === content?.id ? null : content));
    };

    const renderCategory = (value, index) => {
        if (value?.feature === 1) return null;

        const categorySelected = selectedContent?.id === value.id;
        return (
            <div
                className={clsx(
                    "cursor-pointer flex flex-row justify-between items-center mb-2 p-2",
                    categorySelected && "text-primary_color md:bg-accent_color2 rounded-xl",
                )}
                key={index}
                onMouseEnter={() => {
                    if (!isMobile) {
                        handleMainHeadingClick(value);
                    }
                }}
                onClick={() =>
                    categorySelected ? handleMainHeadingClick(null) : handleMainHeadingClick(value)
                }
            >
                {value.heading}
                <i className={"pi pi-chevron-right hidden md:block"} aria-hidden="true"></i>
                <i
                    className={clsx(
                        "block md:hidden",
                        categorySelected ? "pi pi-chevron-up" : "pi pi-chevron-down",
                    )}
                    aria-hidden="true"
                ></i>
            </div>
        );
    };

    const renderTitleLink = (content) => {
        return (
            <Link to={getLink(content.content_reference)}>
                <div className="font-medium uppercase mb-2 mt-2 px-4 md:mt-0 md:px-0 text-primary_color md:text-accent_color1">
                    {content.heading}
                </div>
            </Link>
        );
    };

    const renderSubContentLink = (item, index) => {
        return (
            <Link key={index} to={getLink(item.content_reference)}>
                <div
                    className={clsx(
                        "py-3 px-4 md:px-0",
                        item.feature === 1 ? "md:font-bold" : "font-normal",
                    )}
                >
                    {item.heading}
                    {item.feature === 1 && (
                        <i
                            className="hidden md:inline-block pi pi-arrow-right mx-2"
                            aria-hidden="true"
                        ></i>
                    )}
                </div>
            </Link>
        );
    };

    return (
        <div className={isMobile ? "" : "container menu-content flex"}>
            <div className="flex flex-col text-accent_color1 py-6 text-sm flex-1 items-start">
                {showData && (
                    <>
                        <div
                            className={`w-full items-center justify-between ${
                                isMobile ? "block" : "flex"
                            }`}
                        >
                            <div className="border-solid border-b border-b-accent_color2 pb-4 mb-6">
                                <img
                                    src={data.section_image?.src}
                                    className="h-11 w-11 object-contain mb-4"
                                />
                                <div className="capitalize text-base">{data.heading}</div>
                            </div>

                            {featureItem && (
                                <Link to={getLink(featureItem.content_reference, featureItem.type)}>
                                    <img
                                        src={featureItem.content_image?.src}
                                        className={isMobile ? "pb-4" : ""}
                                    />
                                </Link>
                            )}
                        </div>
                    </>
                )}

                {/* desktop */}
                {showData && (
                    <div
                        ref={containerRef}
                        className="hidden md:flex flex-row w-full h-full overflow-hidden"
                    >
                        <div
                            className="flex flex-col col-3 ltr:mr-8 rtl:ml-8 overflow-y-auto"
                            style={{ maxHeight: availableHeight, overflowY: "auto" }}
                        >
                            {data.contents?.map((value, index) => renderCategory(value, index))}
                        </div>

                        <div
                            className="flex flex-row flex-nowrap gap-x-8 flex-1 col-9 p-2 overflow-y-auto"
                            style={{
                                maxHeight: availableHeight,
                                overflowY: "auto",
                            }}
                        >
                            {selectedContent?.contents?.map((subContent, subIndex) => (
                                <div className="flex flex-col" key={subIndex}>
                                    {renderTitleLink(subContent)}
                                    {subContent.contents?.map((item, index) =>
                                        renderSubContentLink(item, index),
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* mobile */}
                <div className="md:hidden w-full">
                    {data.contents?.map((value, index) => {
                        const isSelected = selectedContent?.id === value.id;
                        return (
                            <div key={index}>
                                {renderCategory(value, index)}
                                {value?.contents?.map((subContent, subIndex) => (
                                    <div
                                        className={clsx(isSelected ? "flex flex-col" : "hidden")}
                                        key={subIndex}
                                    >
                                        {renderTitleLink(subContent)}
                                        {subContent.contents?.map((item, index) =>
                                            renderSubContentLink(item, index),
                                        )}
                                    </div>
                                ))}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

export default SubMenuStyle4;
